import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bookmark, Briefcase, CalendarClock, ListChecks, Plus, Sparkles, Wrench, X } from "lucide-react";
import { useUserInfo } from "../../context/user";
import { useUsersContext } from "../../context/usersContext";
import { useTaskDrafts } from "../../queries/taskDrafts";
import { useShortcuts } from "../../hook/useShortcuts";
import { subscribeShortcutHighlight } from "../../lib/shortcuts";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { buildAuthedPath } from "lib/buildAuthedPath";

/**
 * Primary-nav zone of the unified app shell left sidebar (Lovable AppSidebar
 * zone-1). A vertical list of the six core destinations, each a ghost-button
 * row with a lucide icon + label (and an optional live count badge).
 *
 * Routes use the same encoded authed deep-link pattern as the now-removed legacy Navbar:
 * when both the active user email and active location id are valid, the path is
 * prefixed with `btoa("<email>:<locationId>")`; otherwise the bare `/app/...`
 * path is used. Active-route highlight is driven by useLocation().
 */
export default function AppNav({ collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useUserInfo();
  const user = userInfo[0];
  const activeLocation = userInfo[4];
  const isValidEmail = userInfo[6];
  const isValidLocationId = userInfo[7];

  const { tasksData } = useUsersContext() || {};

  const tasksCount = (tasksData?.prompt || []).filter((t) => {
    const type = String(t?.type);
    return type !== "SuperAdmin" && type !== "super-admin";
  }).length;

  const { data: drafts = [] } = useTaskDrafts(activeLocation?.id);
  const draftsCount = drafts.length;

  const { shortcuts, remove: removeShortcut } = useShortcuts();

  // Transient highlight signal from the "Save this view" pop-up's "Show me"
  // button (ConversationWrapper), fired through lib/shortcuts.js's
  // subscribe/notify since that pop-up and this sidebar are separate
  // component trees under AppShell.
  const [highlightedId, setHighlightedId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  useEffect(() => {
    const unsubscribe = subscribeShortcutHighlight((id) => {
      setHighlightedId(id);
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedId(null), 2000);
    });
    return () => {
      unsubscribe();
      clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  // Build the encoded authed deep-link via the shared helper. Falls back to
  // the bare path when the email/location are missing or fail validation.
  const buildPath = (path) =>
    buildAuthedPath(path, {
      email: user?.email,
      locationId: activeLocation?.id,
      isValidEmail,
      isValidLocationId,
    });

  const destinations = [
    // New Task lands on the chat home (same target as the brand logo), matching
    // the Lovable shell where New Task clears the chat and returns to the
    // landing. The create/schedule form lives under "Automate Tasks" below.
    { label: "New Task", path: "/app/chat", Icon: Plus },
    {
      label: "Your Tasks",
      path: "/app/chat",
      // Lands on the chat home and expands its Task Library (the standalone
      // All-Tasks page was merged into the landing under this anchor).
      hash: "#task-library",
      Icon: Briefcase,
      count: tasksCount,
    },
    {
      label: "Automate Tasks",
      path: "/app/chat/tasks/new",
      Icon: CalendarClock,
    },
    {
      label: "Drafts",
      path: "/app/chat/drafts",
      Icon: ListChecks,
      count: draftsCount,
    },
    {
      label: "Your Tools",
      path: "/app/settings",
      Icon: Wrench,
      state: { tab: "Integrations" },
    },
    { label: "Agents", path: "/app/ai-agents", Icon: Sparkles },
  ];

  // New Task and Your Tasks share the /app/chat path and are told apart by the
  // #task-library hash (Your Tasks anchors to the library; New Task does not).
  const isActive = ({ path, hash }) => {
    if (!location.pathname.endsWith(path)) return false;
    if (hash) return location.hash === hash;
    if (path === "/app/chat") return location.hash !== "#task-library";
    return true;
  };

  return (
    <nav aria-label="Primary" className="flex flex-col gap-1">
      {destinations.map(({ label, path, hash, Icon, count, state }) => {
        const active = isActive({ path, hash });
        const target = `${buildPath(path)}${hash || ""}`;
        return (
          <Button
            key={label}
            type="button"
            variant="ghost"
            onClick={() =>
              state ? navigate(target, { state }) : navigate(target)
            }
            aria-current={active ? "page" : undefined}
            aria-label={label}
            title={collapsed ? label : undefined}
            className={cn(
              "h-9 text-sm font-normal text-muted-foreground border-0",
              collapsed
                ? "w-9 mx-auto justify-center px-0"
                : "w-full justify-start gap-2 px-3",
              active
                ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <Icon size={16} aria-hidden="true" />
            {!collapsed && <span className="flex-1 text-left">{label}</span>}
            {!collapsed && count > 0 && (
              <span
                aria-label={`${count} ${label}`}
                className="ml-auto text-xs text-muted-foreground/80"
              >
                ({count})
              </span>
            )}
          </Button>
        );
      })}

      {shortcuts.length > 0 && (
        <div className="mt-3 flex flex-col gap-1">
          {!collapsed && (
            <span className="px-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
              Shortcuts
            </span>
          )}
          {shortcuts.map((shortcut) => (
            <div key={shortcut.id} className="group relative">
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  navigate(
                    buildPath(
                      shortcut.link ||
                        `/app/chat?${shortcut.search.replace(/^\?/, "")}#task-library`,
                    ),
                  )
                }
                aria-label={shortcut.name}
                title={collapsed ? shortcut.name : undefined}
                className={cn(
                  // text-foreground (not the muted-foreground the other nav
                  // rows use) so the name is white in dark mode / black in
                  // light mode, per reference — the icon above keeps its own
                  // fixed text-ring regardless.
                  "h-9 text-sm font-normal text-foreground border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  // Extra right padding (vs. the other nav rows' plain px-3)
                  // reserves room for the absolutely-positioned remove button
                  // below, so a long truncated name never runs under it.
                  collapsed ? "w-9 mx-auto justify-center px-0" : "w-full justify-start gap-2 pl-3 pr-7",
                  highlightedId === shortcut.id && "ring-2 ring-ring bg-ring/10",
                )}
              >
                {/* Fixed accent color, independent of the row's own text
                    color/highlight state — the bookmark is always this hue,
                    per reference. */}
                <Bookmark size={16} className="text-ring" aria-hidden="true" />
                {!collapsed && <span className="flex-1 min-w-0 text-left truncate">{shortcut.name}</span>}
              </Button>
              {!collapsed && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeShortcut(shortcut.id);
                  }}
                  aria-label={`Remove ${shortcut.name} shortcut`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
